import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'

export type RenewalTimelineView = 'week' | 'month'
export type RenewalLikelihoodLevel = 'สูง' | 'กลาง' | 'ต่ำ'
export type RenewalFollowStatus =
  | 'ยังไม่ติดต่อ'
  | 'ติดต่อแล้ว'
  | 'นัดคุยแล้ว'
  | 'ปฏิเสธ'
  | 'ต่ออายุแล้ว'
export type RenewalPackageTypeLabel =
  | 'รายเดือน'
  | 'รายปี'
  | 'PT Package'
  | 'กลุ่ม/Family'
  | 'คลาสพิเศษ'
export type RenewalExpiryBucket = '0-30' | '31-60' | '61-90' | 'overdue'
export type RenewalAttendanceTrend = 'up' | 'stable' | 'down'
export type RenewalAddonLevel = 'สูง' | 'กลาง' | 'ไม่มี'
export type RenewalSortField = 'expiryDays' | 'packageValue' | 'likelihoodScore' | 'weeklyCheckins'
export type RenewalSortOrder = 'asc' | 'desc'

@Serializable()
export class RenewalSummaryBucket extends Fromable {
  @JsonProperty('count')
  count!: number

  @JsonProperty('totalValue')
  totalValue!: number

  @JsonProperty('deltaCount')
  deltaCount!: number

  @JsonProperty('deltaValue')
  deltaValue!: number
}

@Serializable()
export class RenewalSummary extends Fromable {
  @JsonProperty({ path: 'd0_30', type: RenewalSummaryBucket })
  d0_30!: RenewalSummaryBucket

  @JsonProperty({ path: 'd31_60', type: RenewalSummaryBucket })
  d31_60!: RenewalSummaryBucket

  @JsonProperty({ path: 'd61_90', type: RenewalSummaryBucket })
  d61_90!: RenewalSummaryBucket

  @JsonProperty({ path: 'overdue', type: RenewalSummaryBucket })
  overdue!: RenewalSummaryBucket
}

@Serializable()
export class RenewalTimelinePeriod extends Fromable {
  @JsonProperty('label')
  label!: string

  @JsonProperty('startDate')
  startDate!: string

  @JsonProperty('endDate')
  endDate!: string

  @JsonProperty('series')
  series!: Record<string, number>
}

@Serializable()
export class RenewalTimeline extends Fromable {
  @JsonProperty('view')
  view!: RenewalTimelineView

  @JsonProperty('target')
  target!: number

  @JsonProperty({ path: 'periods', elementType: RenewalTimelinePeriod })
  periods!: RenewalTimelinePeriod[]
}

@Serializable()
export class RenewalMemberFollowLog extends Fromable {
  @JsonProperty('date')
  date!: string

  @JsonProperty('method')
  method!: string

  @JsonProperty('result')
  result!: string

  @JsonProperty('renewalInterest')
  renewalInterest!: string

  @JsonProperty('note')
  note!: string | null
}

@Serializable()
export class RenewalMember extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('userId')
  userId!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('phone')
  phone!: string | null

  @JsonProperty('packageType')
  packageType!: string

  @JsonProperty('packageValue')
  packageValue!: number

  @JsonProperty('expiryDate')
  expiryDate!: string

  @JsonProperty('expiryDays')
  expiryDays!: number

  @JsonProperty('weeklyCheckins')
  weeklyCheckins!: number

  @JsonProperty('renewalCount')
  renewalCount!: number

  @JsonProperty('membershipMonths')
  membershipMonths!: number

  @JsonProperty('addonServices')
  addonServices!: RenewalAddonLevel

  @JsonProperty('attendanceTrend')
  attendanceTrend!: RenewalAttendanceTrend

  @JsonProperty('salesId')
  salesId!: string | null

  @JsonProperty('salesName')
  salesName!: string | null

  @JsonProperty('followStatus')
  followStatus!: RenewalFollowStatus

  @JsonProperty('likelihoodScore')
  likelihoodScore!: number

  @JsonProperty('likelihoodLevel')
  likelihoodLevel!: RenewalLikelihoodLevel

  @JsonProperty({ path: 'followLogs', elementType: RenewalMemberFollowLog })
  followLogs!: RenewalMemberFollowLog[]
}

@Serializable()
export class RenewalMembersPage extends Fromable {
  @JsonProperty('total')
  total!: number

  @JsonProperty('page')
  page!: number

  @JsonProperty('limit')
  limit!: number

  @JsonProperty({ path: 'items', elementType: RenewalMember })
  items!: RenewalMember[]
}

@Serializable()
export class RenewalSalesKpi extends Fromable {
  @JsonProperty('salesId')
  salesId!: string

  @JsonProperty('salesName')
  salesName!: string

  @JsonProperty('nearExpiry')
  nearExpiry!: number

  @JsonProperty('atRiskValue')
  atRiskValue!: number

  @JsonProperty('followedUp')
  followedUp!: number

  @JsonProperty('followupPct')
  followupPct!: number

  @JsonProperty('renewed')
  renewed!: number

  @JsonProperty('renewalRate')
  renewalRate!: number

  @JsonProperty('revenueRetained')
  revenueRetained!: number
}

@Serializable()
export class RenewalAlertSettings extends Fromable {
  @JsonProperty('notify60Days')
  notify60Days!: boolean

  @JsonProperty('notify30Days')
  notify30Days!: boolean

  @JsonProperty('notify7Days')
  notify7Days!: boolean

  @JsonProperty('lineAutoSend')
  lineAutoSend!: boolean

  @JsonProperty('renewalTargetPct')
  renewalTargetPct!: number
}

export interface RenewalMembersQuery {
  branchId?: number
  bucket?: RenewalExpiryBucket
  salesId?: string
  packageType?: RenewalPackageTypeLabel
  likelihood?: RenewalLikelihoodLevel
  followStatus?: RenewalFollowStatus
  sortField?: RenewalSortField
  sortOrder?: RenewalSortOrder
  page?: number
  limit?: number
}

export interface UpdateRenewalAlertSettingsPayload {
  branchId?: number
  notify60Days: boolean
  notify30Days: boolean
  notify7Days: boolean
  lineAutoSend: boolean
  renewalTargetPct: number
}
